import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

export type AiTaskType = 
  | 'product-memo' 
  | 'product-info' 
  | 'cee-memo' 
  | 'cee-analysis'
  | 'cheat-sheet' 
  | 'training' 
  | 'mailing' 
  | 'script'
  | 'eligibility-config';

export type AiTaskStatus = 'queued' | 'pending' | 'success' | 'error';

export interface AiTask {
  id: string;
  type: AiTaskType;
  label: string;
  status: AiTaskStatus;
  queuedAt: Date;
  startedAt?: Date;
  entityId: string;
  error?: string;
  execute: () => Promise<void>;
}

interface AiTasksContextType {
  tasks: AiTask[];
  enqueueTask: (task: Omit<AiTask, 'id' | 'status' | 'queuedAt' | 'startedAt'>) => string;
  getActiveTask: (type: AiTaskType, entityId: string) => AiTask | undefined;
  removeTask: (id: string) => void;
  hasActiveTasks: boolean;
  queuedCount: number;
  pendingCount: number;
}

const MAX_CONCURRENT = 1;

const AiTasksContext = createContext<AiTasksContextType | undefined>(undefined);

export function AiTasksProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<AiTask[]>([]);
  const taskIdCounter = useRef(0);
  const processingRef = useRef(false);

  // Process queue - start next task if possible
  const processQueue = useCallback(() => {
    if (processingRef.current) return;
    
    setTasks(currentTasks => {
      const pendingCount = currentTasks.filter(t => t.status === 'pending').length;
      if (pendingCount >= MAX_CONCURRENT) return currentTasks;

      const nextTask = currentTasks.find(t => t.status === 'queued');
      if (!nextTask) return currentTasks;

      // Mark as pending and start execution
      processingRef.current = true;
      
      const updatedTasks = currentTasks.map(t => 
        t.id === nextTask.id 
          ? { ...t, status: 'pending' as const, startedAt: new Date() }
          : t
      );

      // Execute the task asynchronously
      setTimeout(async () => {
        try {
          await nextTask.execute();
          setTasks(prev => prev.map(t => 
            t.id === nextTask.id ? { ...t, status: 'success' as const } : t
          ));
        } catch (err) {
          setTasks(prev => prev.map(t => 
            t.id === nextTask.id 
              ? { ...t, status: 'error' as const, error: err instanceof Error ? err.message : 'Erreur' }
              : t
          ));
        } finally {
          processingRef.current = false;
        }
      }, 0);

      return updatedTasks;
    });
  }, []);

  // Watch for task status changes to trigger queue processing
  useEffect(() => {
    const pendingCount = tasks.filter(t => t.status === 'pending').length;
    const queuedCount = tasks.filter(t => t.status === 'queued').length;
    
    if (pendingCount < MAX_CONCURRENT && queuedCount > 0 && !processingRef.current) {
      processQueue();
    }
  }, [tasks, processQueue]);

  const enqueueTask = useCallback((taskData: Omit<AiTask, 'id' | 'status' | 'queuedAt' | 'startedAt'>) => {
    const id = `ai-task-${++taskIdCounter.current}-${Date.now()}`;
    const newTask: AiTask = {
      ...taskData,
      id,
      status: 'queued',
      queuedAt: new Date(),
    };
    
    setTasks(prev => [...prev, newTask]);
    
    // Trigger queue processing after state update
    setTimeout(() => processQueue(), 0);
    
    return id;
  }, [processQueue]);

  const removeTask = useCallback((id: string) => {
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  const getActiveTask = useCallback((type: AiTaskType, entityId: string) => {
    return tasks.find(t => 
      t.type === type && 
      t.entityId === entityId && 
      (t.status === 'pending' || t.status === 'queued')
    );
  }, [tasks]);

  const hasActiveTasks = tasks.some(t => t.status === 'pending' || t.status === 'queued');
  const queuedCount = tasks.filter(t => t.status === 'queued').length;
  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  return (
    <AiTasksContext.Provider value={{ 
      tasks, 
      enqueueTask, 
      removeTask, 
      getActiveTask, 
      hasActiveTasks,
      queuedCount,
      pendingCount
    }}>
      {children}
    </AiTasksContext.Provider>
  );
}

export function useAiTasks() {
  const context = useContext(AiTasksContext);
  if (!context) {
    throw new Error('useAiTasks must be used within an AiTasksProvider');
  }
  return context;
}
